import _ from "lodash";
import { BillToCompanyMappingConstant, billToCompanyMappingRequiredFieldList } from "presentation/constant/BillToCompanyMapping/BillToCompanyMappingConstant";
import { statusDropdownOptions } from "presentation/constant/DropDownOptions/StaticDropdownOptions";
import { useBillToCompanyMappingVM } from "presentation/hook/BillToCompanyMapping/useBillToCompanyMappingVM";
import { useMessageBarVM } from "presentation/hook/useMessageBar";
import { useANAInfoTracked } from "presentation/store/ANAInfo";
import { useBillToCompanyMappingTracked } from "presentation/store/BillToCompanyMapping/BillToCompanyMappingProvider";
import { CriteriaItemContainer } from "presentation/view/components/CriteriaItemContainer";
import { NbisCommonField } from "presentation/view/components/NbisCommonField";
import { NbisIconButton } from "presentation/view/components/NbisIconButton";
import { memo, useCallback, useMemo, useState } from "react";
import { FieldType, HPHBreadcrumb, HPHCheckbox, HPHGroupTitle, IconButton, IFieldValue, Loader } from "veronica-ui-component/dist/component/core";
import { Breadcrumb, SidebarCaption, Sidebarheader, SidebarTitle, StyledAction, StyledSidebar } from "veronica-ui-component/dist/component/core/styled/uiFramework.styled";

const BillToCompanyMappingEditPanel = () => {
    const BILL_TO_COMPANY_MAPPING_CONSTANT = BillToCompanyMappingConstant.Table;
    const [billToCompanyMappingState] = useBillToCompanyMappingTracked();
    const billToCompanyMappingVM = useBillToCompanyMappingVM();
    const { currentEditRow, isAdd, isEdit, masterState } = billToCompanyMappingState;
    const { isSaveClicked, allFormState } = masterState;
    const [isLoading, setIsLoading] = useState(false);
    const messageBarVM = useMessageBarVM();
    const [anaInfoState] = useANAInfoTracked();
    const { allowUpdate } = anaInfoState;

    const memoSubChgTypeOptions = useMemo(() => {
        if (!currentEditRow.chargeType) {
            const allCommonSubChgTypeOptions = Object.values(billToCompanyMappingState.dynamicOptions.subChargeTypeDropdownOptions).flat();
            return _.orderBy(_.uniqBy(_.flatMap(_.concat(allCommonSubChgTypeOptions ?? [])), 'value'), 'dropdownLabel');
        }

        const subChgTypeOptions = currentEditRow.chargeType ? billToCompanyMappingState.dynamicOptions.subChargeTypeDropdownOptions[currentEditRow.chargeType] : [];
        return subChgTypeOptions;
    }, [currentEditRow.chargeType, billToCompanyMappingState.dynamicOptions.subChargeTypeDropdownOptions])

    // const memoOpsLineCompanyOptions = useMemo(() => {
    //     if (operatingUnit === 'SAPT' || operatingUnit === 'KICT') {
    //         const opsLineCompanyeOptions = billToCompanyMappingState.dynamicOptions.opsLineCompanyDropdownOptions;
    //         return opsLineCompanyeOptions;
    //     } else {
    //         const opsLineCompanyeOptions = billToCompanyMappingState.dynamicOptions.opsLineDropdownOptions;
    //         return opsLineCompanyeOptions;
    //     }
    // }, [operatingUnit, billToCompanyMappingState.dynamicOptions.opsLineCompanyDropdownOptions, billToCompanyMappingState.dynamicOptions.opsLineDropdownOptions])

    const memoChargeType = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "260px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={!(isAdd || isEdit)}
                isShowOptional={true}
                readOnlyValue={currentEditRow?.chargeType || ''}
                fieldValue={currentEditRow?.chargeType}
                fieldLabel={BILL_TO_COMPANY_MAPPING_CONSTANT.CHARGE_TYPE}
                isSaveClicked={isSaveClicked}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'chargeType'}
                maxLength={60}
                options={billToCompanyMappingState.dynamicOptions.chargeTypeDropdownOptions}
                requiredFieldList={billToCompanyMappingRequiredFieldList}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    billToCompanyMappingVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [BILL_TO_COMPANY_MAPPING_CONSTANT.CHARGE_TYPE, allFormState, billToCompanyMappingState.dynamicOptions.chargeTypeDropdownOptions, billToCompanyMappingVM, currentEditRow?.chargeType, isAdd, isEdit, isSaveClicked])

    const memoSubChargeType = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "260px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={!(isAdd || isEdit)}
                isShowOptional={true}
                readOnlyValue={currentEditRow?.subChargeType || ''}
                fieldValue={currentEditRow?.subChargeType}
                fieldLabel={BILL_TO_COMPANY_MAPPING_CONSTANT.SUB_CHARGE_TYPE}
                isSaveClicked={isSaveClicked}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'subChargeType'}
                maxLength={60}
                options={memoSubChgTypeOptions}
                requiredFieldList={billToCompanyMappingRequiredFieldList}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    billToCompanyMappingVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [BILL_TO_COMPANY_MAPPING_CONSTANT.SUB_CHARGE_TYPE, allFormState, billToCompanyMappingVM, currentEditRow?.subChargeType, isAdd, isEdit, isSaveClicked, memoSubChgTypeOptions])

    const memoSoa = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "260px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={!(isAdd || isEdit)}
                isShowOptional={true}
                readOnlyValue={currentEditRow?.soa || ''}
                fieldValue={currentEditRow?.soa}
                fieldLabel={BILL_TO_COMPANY_MAPPING_CONSTANT.SOA}
                isSaveClicked={isSaveClicked}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'soa'}
                maxLength={60}
                options={billToCompanyMappingState.dynamicOptions.soaDropdownOptions}
                requiredFieldList={billToCompanyMappingRequiredFieldList}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    billToCompanyMappingVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [BILL_TO_COMPANY_MAPPING_CONSTANT.SOA, allFormState, billToCompanyMappingState.dynamicOptions.soaDropdownOptions, billToCompanyMappingVM, currentEditRow?.soa, isAdd, isEdit, isSaveClicked])

    const memoServiceCode = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "260px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={!(isAdd || isEdit)}
                isShowOptional={true}
                readOnlyValue={currentEditRow?.serviceCode || ''}
                fieldValue={currentEditRow?.serviceCode}
                fieldLabel={BILL_TO_COMPANY_MAPPING_CONSTANT.SERVICE}
                isSaveClicked={isSaveClicked}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'serviceCode'}
                maxLength={60}
                options={billToCompanyMappingState.dynamicOptions.serviceCodeDropdownOptions}
                requiredFieldList={billToCompanyMappingRequiredFieldList}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    billToCompanyMappingVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [BILL_TO_COMPANY_MAPPING_CONSTANT.SERVICE, allFormState, billToCompanyMappingState.dynamicOptions.serviceCodeDropdownOptions, billToCompanyMappingVM, currentEditRow?.serviceCode, isAdd, isEdit, isSaveClicked])

    const memoOpsLine = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "260px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={!(isAdd || isEdit)}
                isShowOptional={true}
                readOnlyValue={currentEditRow?.owner || ''}
                fieldValue={currentEditRow?.owner}
                fieldLabel={BILL_TO_COMPANY_MAPPING_CONSTANT.OPS_LINE}
                isSaveClicked={isSaveClicked}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'owner'}
                maxLength={60}
                options={billToCompanyMappingState.dynamicOptions.opsLineDropdownOptions}
                requiredFieldList={billToCompanyMappingRequiredFieldList}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    billToCompanyMappingVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [BILL_TO_COMPANY_MAPPING_CONSTANT.OPS_LINE, allFormState, billToCompanyMappingState.dynamicOptions.opsLineDropdownOptions, billToCompanyMappingVM, currentEditRow?.owner, isAdd, isEdit, isSaveClicked])

    const memoForwarderReference = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "260px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={!(isAdd || isEdit)}
                isShowOptional={true}
                readOnlyValue={currentEditRow?.forwarderReference || ''}
                fieldValue={currentEditRow?.forwarderReference}
                fieldLabel={BILL_TO_COMPANY_MAPPING_CONSTANT.FORWARDER_REF}
                isSaveClicked={isSaveClicked}
                fieldType={FieldType.TEXT}
                fieldKey={'forwarderReference'}
                maxLength={50}
                requiredFieldList={billToCompanyMappingRequiredFieldList}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    billToCompanyMappingVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [BILL_TO_COMPANY_MAPPING_CONSTANT.FORWARDER_REF, allFormState, billToCompanyMappingVM, currentEditRow?.forwarderReference, isAdd, isEdit, isSaveClicked])

    const memoCntrStatus = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "260px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={!(isAdd || isEdit)}
                isShowOptional={true}
                readOnlyValue={currentEditRow?.cntrStatus || ''}
                fieldValue={currentEditRow?.cntrStatus}
                fieldLabel={BILL_TO_COMPANY_MAPPING_CONSTANT.CONTAINER_STATUS}
                isSaveClicked={isSaveClicked}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'cntrStatus'}
                maxLength={60}
                options={statusDropdownOptions}
                requiredFieldList={billToCompanyMappingRequiredFieldList}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    billToCompanyMappingVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [BILL_TO_COMPANY_MAPPING_CONSTANT.CONTAINER_STATUS, allFormState, billToCompanyMappingVM, currentEditRow?.cntrStatus, isAdd, isEdit, isSaveClicked])

    const memoBillToCompanyCode = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "260px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={!(isAdd || isEdit)}
                isShowOptional={true}
                readOnlyValue={currentEditRow?.billToCompanyCode || ''}
                fieldValue={currentEditRow?.billToCompanyCode}
                fieldLabel={BILL_TO_COMPANY_MAPPING_CONSTANT.BILLTO_COMPANY}
                isSaveClicked={isSaveClicked}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'billToCompanyCode'}
                maxLength={60}
                sort={false}
                isLimitOptions={true}
                options={billToCompanyMappingState.dynamicOptions.billToCompanyDropdownOptions}
                requiredFieldList={billToCompanyMappingRequiredFieldList}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    billToCompanyMappingVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [BILL_TO_COMPANY_MAPPING_CONSTANT.BILLTO_COMPANY, allFormState, billToCompanyMappingState.dynamicOptions.billToCompanyDropdownOptions, billToCompanyMappingVM, currentEditRow?.billToCompanyCode, isAdd, isEdit, isSaveClicked])

    const memoVesselType = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "260px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={!(isAdd || isEdit)}
                isShowOptional={true}
                readOnlyValue={currentEditRow?.vesselType || ''}
                fieldValue={currentEditRow?.vesselType}
                fieldLabel={BILL_TO_COMPANY_MAPPING_CONSTANT.VESSEL_TYPE}
                isSaveClicked={isSaveClicked}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'vesselType'}
                maxLength={60}
                options={billToCompanyMappingState.dynamicOptions.vesselTypeDropdownOptions}
                requiredFieldList={billToCompanyMappingRequiredFieldList}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    billToCompanyMappingVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [BILL_TO_COMPANY_MAPPING_CONSTANT.VESSEL_TYPE, allFormState, billToCompanyMappingState.dynamicOptions.vesselTypeDropdownOptions, billToCompanyMappingVM, currentEditRow?.vesselType, isAdd, isEdit, isSaveClicked])

    const memoConsortiumCode = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "260px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={!(isAdd || isEdit)}
                isShowOptional={true}
                readOnlyValue={currentEditRow?.consortiumCode || ''}
                fieldValue={currentEditRow?.consortiumCode}
                fieldLabel={BILL_TO_COMPANY_MAPPING_CONSTANT.CONSORTIUM}
                isSaveClicked={isSaveClicked}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'consortiumCode'}
                maxLength={60}
                options={billToCompanyMappingState.dynamicOptions.consortiumCodeDropdownOptions}
                requiredFieldList={billToCompanyMappingRequiredFieldList}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    billToCompanyMappingVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [BILL_TO_COMPANY_MAPPING_CONSTANT.CONSORTIUM, allFormState, billToCompanyMappingState.dynamicOptions.consortiumCodeDropdownOptions, billToCompanyMappingVM, currentEditRow?.consortiumCode, isAdd, isEdit, isSaveClicked])

    const memoForwarderCode = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "260px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={!(isAdd || isEdit)}
                isShowOptional={true}
                readOnlyValue={currentEditRow?.forwarderCode || ''}
                fieldValue={currentEditRow?.forwarderCode}
                fieldLabel={BILL_TO_COMPANY_MAPPING_CONSTANT.FORWARDER_CODE}
                isSaveClicked={isSaveClicked}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'forwarderCode'}
                maxLength={60}
                options={billToCompanyMappingState.dynamicOptions.forwarderCodeDropdownOptions}
                requiredFieldList={billToCompanyMappingRequiredFieldList}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    billToCompanyMappingVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [BILL_TO_COMPANY_MAPPING_CONSTANT.FORWARDER_CODE, allFormState, billToCompanyMappingState.dynamicOptions.forwarderCodeDropdownOptions, billToCompanyMappingVM, currentEditRow?.forwarderCode, isAdd, isEdit, isSaveClicked])

    const memoOperatingTml = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "260px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={!(isAdd || isEdit)}
                isShowOptional={true}
                readOnlyValue={currentEditRow?.operatingTml || ''}
                fieldValue={currentEditRow?.operatingTml}
                fieldLabel={BILL_TO_COMPANY_MAPPING_CONSTANT.OPERATING_TML}
                isSaveClicked={isSaveClicked}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'operatingTml'}
                maxLength={60}
                options={billToCompanyMappingState.dynamicOptions.operatingTmlDropdownOptions}
                requiredFieldList={billToCompanyMappingRequiredFieldList}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    billToCompanyMappingVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [BILL_TO_COMPANY_MAPPING_CONSTANT.OPERATING_TML, allFormState, billToCompanyMappingState.dynamicOptions.operatingTmlDropdownOptions, billToCompanyMappingVM, currentEditRow?.operatingTml, isAdd, isEdit, isSaveClicked])

    const memoChargeCode = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "260px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={!(isAdd || isEdit)}
                isShowOptional={true}
                readOnlyValue={currentEditRow?.chargeCode || ''}
                fieldValue={currentEditRow?.chargeCode}
                fieldLabel={BILL_TO_COMPANY_MAPPING_CONSTANT.CHARGE_CODE}
                isSaveClicked={isSaveClicked}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'chargeCode'}
                maxLength={60}
                options={billToCompanyMappingState.dynamicOptions.chargeCodeDropdownOptions}
                requiredFieldList={billToCompanyMappingRequiredFieldList}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    billToCompanyMappingVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [BILL_TO_COMPANY_MAPPING_CONSTANT.CHARGE_CODE, allFormState, billToCompanyMappingState.dynamicOptions.chargeCodeDropdownOptions, billToCompanyMappingVM, currentEditRow?.chargeCode, isAdd, isEdit, isSaveClicked])

    const memoActiveInd = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "260px", marginBottom: "24px" }}>
            <NbisCommonField
                fieldKey={"activeInd"}
                requiredFieldList={billToCompanyMappingRequiredFieldList}
                fieldLabel={BILL_TO_COMPANY_MAPPING_CONSTANT.ACTIVE_IND}
                fieldType={FieldType.TEXT}
                isReadOnly={true}
                onFieldChange={() => { }} />
            <HPHCheckbox
                label={""}
                checked={currentEditRow?.activeInd === 'Y'}
                disabled={!(isAdd || isEdit)}
                onChange={(e) => billToCompanyMappingVM.onCheckboxChange(e.checked, "activeInd")}
            />
        </div>
        , [BILL_TO_COMPANY_MAPPING_CONSTANT.ACTIVE_IND, billToCompanyMappingVM, currentEditRow?.activeInd, isAdd, isEdit])

    const handleSave = useCallback(async () => {
        // if (!currentEditRow.billToCompanyCode) {
        //     messageBarVM.showError("Bill-To Company is mandatory.");
        //     return;
        // }
        // if (!currentEditRow.activeInd) {
        //     messageBarVM.showError("Active Ind is mandatory.");
        //     return;
        // }
        setIsLoading(true);
        billToCompanyMappingVM.onSaveClicked();
        const data = await billToCompanyMappingVM.onSave(currentEditRow);
        if (data && data.success) {
            messageBarVM.showSuccess("Save Successfully.");
            billToCompanyMappingVM.searchAllBillToCompanyMappingList().then((data) => {
                setIsLoading(false)
            }).catch(error => {
                setIsLoading(false)
            });
        } else {
            setIsLoading(false)
            data.data && messageBarVM.showError(data.data);
            return;
        }
    }, [billToCompanyMappingVM, currentEditRow, messageBarVM]);

    const handClose = useCallback(() => {
        billToCompanyMappingVM.onCloseEidtPanel();
    }, [billToCompanyMappingVM]);

    const handleEdit = useCallback(() => {
        billToCompanyMappingVM.onEditClick();
    }, [billToCompanyMappingVM]);

    const handleReset = useCallback(() => {
        billToCompanyMappingVM.onResetClick(isAdd);
    }, [billToCompanyMappingVM, isAdd]);

    return <>
        <StyledSidebar style={{ width: '100%', height: "100%", border: 'none', backgroundColor: '#FFFFFF', overflow: 'auto', padding: '0' }} position='right'>
            <Sidebarheader style={{ width: '100%' }}>
                {isLoading && <Loader Indicator="Spinner" size="Medium" />}
                <Breadcrumb>
                    <HPHBreadcrumb breadcrumbData={[{ title: BillToCompanyMappingConstant.Title.CATEGORY },
                    { title: BillToCompanyMappingConstant.Title.TITLE }]} onCurrentClick={() => { }}>
                    </HPHBreadcrumb>
                </Breadcrumb>
                <StyledAction>
                    <>
                        {allowUpdate && (!(isAdd || isEdit)) && <NbisIconButton fileName='Icon-pen' size='medium' disabled={false} toolTipArrow={false} toolTipPlacement="bottom" toolTipText={'Edit'} onClick={handleEdit} />}
                        {(isAdd || isEdit) && <IconButton fileName="Icon-reset" size="medium" disabled={false} toolTipArrow={false} toolTipPlacement="bottom" toolTipText={'Reset'} onClick={() => handleReset()} />}
                        <IconButton fileName="Icon-cross" size="medium" disabled={false} toolTipArrow={false} toolTipPlacement='bottom' toolTipText={'Close'} onClick={() => handClose()} />
                        {(isAdd || isEdit) && <IconButton fileName="Icon-tick" size="medium" disabled={false} toolTipArrow={false} toolTipPlacement="bottom" toolTipText={'Save'} onClick={() => handleSave()} />}
                    </>
                </StyledAction>
            </Sidebarheader>



            <div className={'add-edit-form'} style={{ height: 'calc(100vh - 125px)', maxHeight: 'calc(100vh - 125px)', overflow: 'auto' }}>
                <div className="flex-row-auto">
                    <div className={'flex-row-item'}>
                        <div className={'im-flex-row'}>
                            <div className={'flex-row-item-stretch'}>
                                <SidebarTitle>
                                    {isAdd && "New Bill-To Company Setting"}
                                    {!isAdd && (currentEditRow.priority + "/" + currentEditRow.billToCompanyCode)}
                                </SidebarTitle>
                                {<SidebarCaption>"All fields are required unless indicated as 'optional'"</SidebarCaption>}
                            </div>
                        </div>
                        <br />
                        <HPHGroupTitle titleText={BillToCompanyMappingConstant.Title.MAPPING_FROM} />
                        <CriteriaItemContainer>
                            {memoChargeType}
                            {memoSubChargeType}
                            {memoOpsLine}
                        </CriteriaItemContainer>

                        {/* <label>CONTAINER</label> */}
                        <CriteriaItemContainer>
                            {memoCntrStatus}
                            {memoForwarderCode}
                            {memoForwarderReference}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {memoChargeCode}
                            {memoServiceCode}
                            {memoVesselType}
                        </CriteriaItemContainer>

                        {/* <label>VOYAGE</label> */}
                        <CriteriaItemContainer>
                            {memoConsortiumCode}
                            {memoOperatingTml}
                            {memoSoa}
                        </CriteriaItemContainer>

                        <HPHGroupTitle titleText={BillToCompanyMappingConstant.Title.MAPPING_TO} />
                        <CriteriaItemContainer>
                            {memoBillToCompanyCode}
                            {memoActiveInd}
                        </CriteriaItemContainer>
                    </div>
                </div>
            </div>
        </StyledSidebar>
    </>
}

export default memo(BillToCompanyMappingEditPanel);
